import React, { useCallback, useEffect, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { SPWDialogWithTitle } from 'spw-shared/globalComponents';
import { colors } from 'spw-theme';
import { IProject, UserRoleInProject } from 'spw-redux/modules/projects/types';
import { useDispatch } from 'react-redux';
import { SearchInput } from 'spw-shared/globalComponents/input';
import { USER_SEARCH_PLACEHOLDER_TEXT } from 'spw-constants';
import { IUser } from 'spw-redux/modules/user/types';
import {
  leaveProject,
  transferProjectOwnership,
} from 'spw-redux/modules/projects/actions';
import { useHistory } from 'react-router-v5';
import { useUserSuggestions } from '../users/hooks';

const useStyle = makeStyles({
  paper: {
    width: '392px',
  },
  description: {
    marginBottom: 24,
  },
  directions: {
    marginBottom: 8,
  },
  searchUsersInput: {
    marginBottom: 32,
  },
});

type Props = {
  isVisible: boolean;
  onClose: () => void;
  project: IProject;
  onLeaveProjectSuccess?: (projectId: number) => void;
};

const getLeaveProjectDescriptionByUserRole = (userRole: UserRoleInProject) => {
  switch (userRole) {
    case UserRoleInProject.projectOwner:
      return 'You are the project owner and cannot leave the project until project ownership is transferred to another person.';

    case UserRoleInProject.orgAdmin:
      return 'As an Org Admin, you will not lose edit access to a project when you leave.';
    default:
      return 'Leaving a project removes your edit access. You can rejoin as a Project Editor however some projects require approval to rejoin.';
  }
};

const LeaveProjectDialog = ({
  isVisible,
  onClose,
  project,
  onLeaveProjectSuccess,
}: Props) => {
  const [newProjectOwner, setNewProjectOwner] = useState<IUser>(null);
  const dispatch = useDispatch();
  const classes = useStyle();
  const history = useHistory();
  const [searchText, setSearchText] = useState('');
  const userSuggestions = useUserSuggestions(searchText);

  const isProjectOwner =
    project?.currentUserRole === UserRoleInProject.projectOwner;

  const dialogTitle = isProjectOwner
    ? 'Leave Project & Transfer Ownership'
    : 'Leave Project';

  const handleOnLeaveProjectSuccessfully = useCallback(() => {
    if (onLeaveProjectSuccess) {
      onLeaveProjectSuccess(project.id);
    } else {
      history.replace('/home');
    }
  }, [history, onLeaveProjectSuccess, project?.id]);

  const confirmLeaveProject = useCallback(() => {
    onClose();
    if (isProjectOwner) {
      dispatch(
        transferProjectOwnership({
          user: newProjectOwner,
          projectId: project.id,
          preservePreviousOwnerAsEditor: false,
          onSuccess: handleOnLeaveProjectSuccessfully,
        })
      );
    } else {
      dispatch(
        leaveProject({
          projectId: project.id,
          onSuccess: handleOnLeaveProjectSuccessfully,
        })
      );
    }
  }, [
    onClose,
    isProjectOwner,
    handleOnLeaveProjectSuccessfully,
    project,
    newProjectOwner,
    dispatch,
  ]);
  const text = getLeaveProjectDescriptionByUserRole(project?.currentUserRole);

  const saveButtonProps = useMemo(
    () =>
      isProjectOwner
        ? {
            name: 'Leave & transfer',
            onClick: confirmLeaveProject,
            disabled: !newProjectOwner,
            style: {
              width: 'unset',
              paddingRight: '16px',
              paddingLeft: '16px',
              backgroundColor: newProjectOwner
                ? colors.red
                : colors.veryLightGray,
            },
          }
        : {
            name: 'Leave',
            onClick: confirmLeaveProject,
            style: {
              backgroundColor: colors.red,
            },
          },
    [confirmLeaveProject, isProjectOwner, newProjectOwner]
  );

  useEffect(() => {
    setSearchText(newProjectOwner?.name || '');
  }, [newProjectOwner]);

  return (
    <SPWDialogWithTitle
      cancelButtonProps={{
        customColor: colors.black,
        onClick: onClose,
      }}
      PaperProps={{
        className: classes.paper,
      }}
      onClose={onClose}
      open={isVisible}
      saveButtonProps={saveButtonProps}
      title={dialogTitle}
    >
      <div className={classes.description}>{text}</div>

      {isProjectOwner && (
        <>
          <div className={classes.directions}>
            Transfer the project from <b>{project?.projectOwner?.name}</b> to:
          </div>
          <div className={classes.searchUsersInput}>
            <SearchInput<IUser>
              options={userSuggestions.items}
              loading={userSuggestions.areLoading}
              onChange={(newValue) => {
                setNewProjectOwner(newValue);
              }}
              onDelete={() => {
                setNewProjectOwner(null);
              }}
              getOptionDisabled={(option: IUser) =>
                option.id === project?.projectOwner.id
              }
              getOptionLabel={(option: IUser) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              className="search-input"
              textFieldProps={{
                fullWidth: true,
                placeholder: USER_SEARCH_PLACEHOLDER_TEXT,
                onChange: ({ target: { value } }) => {
                  setSearchText(value);
                },
              }}
              value={newProjectOwner}
              inputValue={searchText}
            />
          </div>
        </>
      )}
    </SPWDialogWithTitle>
  );
};

export default LeaveProjectDialog;
